import {
  Alert,
  Button,
  Col,
  Collapse,
  Drawer,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { BookOpen, Check, Info, ShieldCheck, Trash2 } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ProtocolEnum,
  type OrganizationCustomAuthProvider,
  type OrganizationCustomAuthProviderWriteWritable
} from 'src/api/main'
import { DateTimeCell, MonoValue } from 'src/components'
import { DOCS_LINKS } from 'src/constants'

import {
  PROVIDER_LOGO_PRESETS,
  ProviderLogo,
  presetValue
} from '../WorkspaceCustomProvidersPanel/ProviderLogo'

import { useStyles } from './styles'

const { Text, Title } = Typography

const MASKED_SECRET_PLACEHOLDER = '********'
const PROTOCOLS: ProtocolEnum[] = [
  ProtocolEnum.OIDC,
  ProtocolEnum.OAUTH2,
  ProtocolEnum.SAML2
]

type DrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; provider: OrganizationCustomAuthProvider }

type FormValues = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled: boolean
  logo_kind?: 'none' | 'preset' | 'url'
  logo_preset?: string
  logo_url?: string
  client_id?: string
  client_secret?: string
  issuer_url?: string
  authorize_url?: string
  token_url?: string
  userinfo_url?: string
  jwks_url?: string
  scope?: string
  saml_entity_id?: string
  saml_sso_url?: string
  saml_slo_url?: string
  saml_x509_cert?: string
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
  provisioning_mode?: 'just_in_time' | 'approval' | 'explicit'
}

const resolveLogoValue = (values: FormValues): string => {
  if (values.logo_kind === 'preset' && values.logo_preset) {
    return presetValue(values.logo_preset)
  }
  if (values.logo_kind === 'url') {
    return (values.logo_url ?? '').trim()
  }
  return ''
}

const buildPayload = (
  values: FormValues
): OrganizationCustomAuthProviderWriteWritable => {
  const payload: OrganizationCustomAuthProviderWriteWritable = {
    name: values.name,
    slug: values.slug,
    protocol: values.protocol,
    enabled: values.enabled,
    logo: resolveLogoValue(values),
    client_id: values.client_id || undefined,
    issuer_url: values.issuer_url || undefined,
    authorize_url: values.authorize_url || undefined,
    token_url: values.token_url || undefined,
    userinfo_url: values.userinfo_url || undefined,
    jwks_url: values.jwks_url || undefined,
    scope: values.scope || undefined,
    saml_entity_id: values.saml_entity_id || undefined,
    saml_sso_url: values.saml_sso_url || undefined,
    saml_slo_url: values.saml_slo_url || undefined,
    saml_x509_cert: values.saml_x509_cert || undefined,
    email_claim: values.email_claim || undefined,
    first_name_claim: values.first_name_claim || undefined,
    last_name_claim: values.last_name_claim || undefined
  }
  if (
    values.client_secret &&
    values.client_secret !== MASKED_SECRET_PLACEHOLDER
  ) {
    payload.client_secret = values.client_secret
  }
  return payload
}

type CallbackUrlRowProps = {
  label: string
  value: string
}

const CallbackUrlRow = ({ label, value }: CallbackUrlRowProps) => {
  const { styles } = useStyles()

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={8}
      className={styles.callbackRow}
    >
      <Flex vertical style={{ lineHeight: 1.2, minWidth: 0, flex: 1 }}>
        <span className={styles.callbackLabel}>{label}</span>
        <MonoValue>{value}</MonoValue>
      </Flex>
    </Flex>
  )
}

type LabelProps = { label: ReactNode; hint?: ReactNode }

/**
 * Inline label with a small info-icon tooltip. Replaces the verbose
 * `extra={...}` helper text under fields so the form stays compact.
 */
const LabelWithHint = ({ label, hint }: LabelProps) => {
  if (!hint) {
    return <>{label}</>
  }
  return (
    <Space size={4}>
      <span>{label}</span>
      <Tooltip title={hint}>
        <Info
          size={12}
          color="var(--ant-color-text-tertiary)"
          style={{ display: 'inline-block', verticalAlign: 'middle' }}
        />
      </Tooltip>
    </Space>
  )
}

type Props = {
  state: DrawerState
  onClose: () => void
  onSubmit: (body: OrganizationCustomAuthProviderWriteWritable) => void
  onDelete: (providerId: number) => void
  submitting: boolean
  deleting: boolean
}

export const CustomProviderDrawer = ({
  state,
  onClose,
  onSubmit,
  onDelete,
  submitting,
  deleting
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [form] = Form.useForm<FormValues>()

  const isOpen = state.mode !== 'closed'
  const isEdit = state.mode === 'edit'
  const editing = state.mode === 'edit' ? state.provider : null

  const protocolWatch = Form.useWatch('protocol', form)
  const logoKindWatch = Form.useWatch('logo_kind', form)
  const logoPresetWatch = Form.useWatch('logo_preset', form)
  const logoUrlWatch = Form.useWatch('logo_url', form)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    form.resetFields()
    if (editing) {
      let logo_kind: FormValues['logo_kind'] = 'none'
      let logo_preset = ''
      let logo_url = ''
      if (editing.logo?.startsWith('preset:')) {
        logo_kind = 'preset'
        logo_preset = editing.logo.slice('preset:'.length)
      } else if (editing.logo) {
        logo_kind = 'url'
        logo_url = editing.logo
      }
      form.setFieldsValue({
        name: editing.name,
        slug: editing.slug,
        protocol: editing.protocol,
        enabled: editing.enabled,
        logo_kind,
        logo_preset,
        logo_url,
        client_id: editing.client_id ?? '',
        client_secret: editing.client_secret ?? '',
        issuer_url: editing.issuer_url ?? '',
        authorize_url: editing.authorize_url ?? '',
        token_url: editing.token_url ?? '',
        userinfo_url: editing.userinfo_url ?? '',
        jwks_url: editing.jwks_url ?? '',
        scope: editing.scope,
        saml_entity_id: editing.saml_entity_id ?? '',
        saml_sso_url: editing.saml_sso_url ?? '',
        saml_slo_url: editing.saml_slo_url ?? '',
        saml_x509_cert: editing.saml_x509_cert ?? '',
        email_claim: editing.email_claim,
        first_name_claim: editing.first_name_claim,
        last_name_claim: editing.last_name_claim,
        provisioning_mode: 'just_in_time'
      })
    } else {
      form.setFieldsValue({
        protocol: 'oidc',
        enabled: false,
        logo_kind: 'none',
        scope: 'openid email profile',
        email_claim: 'email',
        first_name_claim: 'given_name',
        last_name_claim: 'family_name',
        provisioning_mode: 'just_in_time'
      })
    }
  }, [isOpen, editing, form])

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      size={560}
      destroyOnHidden
      title={
        <Flex align="center" gap={10}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'var(--ant-color-primary-bg)',
              color: 'var(--ant-color-primary-text)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ShieldCheck size={14} />
          </span>
          <Flex vertical style={{ lineHeight: 1.2 }}>
            <Title level={5} style={{ margin: 0, fontSize: 16 }}>
              {isEdit
                ? t('workspace.authentication.customProviders.editProvider')
                : t('workspace.authentication.customProviders.addProvider')}
            </Title>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {isEdit
                ? (editing?.name ?? '')
                : t('workspace.authentication.customProviders.addProviderHint')}
            </Text>
          </Flex>
        </Flex>
      }
      styles={{ body: { paddingTop: 14 } }}
      footer={
        <Flex justify="space-between" align="center" gap={12}>
          <Flex align="center" gap={10}>
            {isEdit && editing ? (
              <Button
                danger
                icon={<Trash2 size={14} />}
                loading={deleting}
                onClick={() => onDelete(editing.id)}
              >
                {t(
                  'workspace.authentication.customProviders.actions.deleteProvider'
                )}
              </Button>
            ) : (
              <Button
                type="link"
                icon={<BookOpen size={14} />}
                href={DOCS_LINKS.addCustomAuthProvider}
                target="_blank"
                rel="noreferrer"
                style={{ paddingInline: 0 }}
              >
                {t('workspace.authentication.customProviders.setupGuide')}
              </Button>
            )}
          </Flex>
          <Flex gap={8} align="center">
            <Form.Item name="enabled" valuePropName="checked" noStyle>
              <EnableInline
                label={t(
                  'workspace.authentication.customProviders.fields.enabled'
                )}
              />
            </Form.Item>
            <Button onClick={onClose}>{t('cancel')}</Button>
            <Button
              type="primary"
              icon={<Check size={14} />}
              loading={submitting}
              onClick={() => form.submit()}
            >
              {isEdit
                ? t('save')
                : t('workspace.authentication.customProviders.createProvider')}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(values) => onSubmit(buildPayload(values))}
      >
        <span className={styles.sectionLabel}>
          {t('workspace.authentication.customProviders.sections.identity')}
        </span>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.displayName'
              )}
              name="name"
              rules={[{ required: true, message: t('required_field_error') }]}
            >
              <Input placeholder="Auth0 production" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label={
                <LabelWithHint
                  label={t(
                    'workspace.authentication.customProviders.fields.slug'
                  )}
                  hint={t(
                    'workspace.authentication.customProviders.fields.slugHelp'
                  )}
                />
              }
              name="slug"
              rules={[
                { required: true, message: t('required_field_error') },
                {
                  pattern: /^[a-z][a-z0-9_-]{1,62}$/,
                  message: t(
                    'workspace.authentication.customProviders.fields.slugInvalid'
                  )
                }
              ]}
            >
              <Input placeholder="auth0-prod" disabled={isEdit} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.protocol'
              )}
              name="protocol"
              rules={[{ required: true, message: t('required_field_error') }]}
            >
              <Select
                options={PROTOCOLS.map((p) => ({
                  value: p,
                  label: t(
                    `workspace.authentication.customProviders.protocols.${p}`
                  )
                }))}
                disabled={isEdit}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label={t('workspace.authentication.customProviders.fields.logo')}
              name="logo_kind"
            >
              <Select
                options={[
                  {
                    value: 'none',
                    label: t(
                      'workspace.authentication.customProviders.fields.logoNone'
                    )
                  },
                  {
                    value: 'preset',
                    label: t(
                      'workspace.authentication.customProviders.fields.logoPreset'
                    )
                  },
                  {
                    value: 'url',
                    label: t(
                      'workspace.authentication.customProviders.fields.logoUrl'
                    )
                  }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        {logoKindWatch === 'preset' ? (
          <Form.Item
            name="logo_preset"
            label={t(
              'workspace.authentication.customProviders.fields.logoPresetPick'
            )}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Select
              options={PROVIDER_LOGO_PRESETS.map((key) => ({
                value: key,
                label: (
                  <Flex align="center" gap={8}>
                    <ProviderLogo value={presetValue(key)} size={16} />
                    <span style={{ textTransform: 'capitalize' }}>{key}</span>
                  </Flex>
                )
              }))}
            />
          </Form.Item>
        ) : null}
        {logoKindWatch === 'url' ? (
          <Form.Item
            name="logo_url"
            label={
              <LabelWithHint
                label={t(
                  'workspace.authentication.customProviders.fields.logoUrlInput'
                )}
                hint={t(
                  'workspace.authentication.customProviders.fields.logoUrlHelp'
                )}
              />
            }
            rules={[
              {
                required: true,
                type: 'url',
                message: t('required_field_error')
              }
            ]}
          >
            <Input placeholder="https://example.com/logo.svg" />
          </Form.Item>
        ) : null}
        {logoKindWatch && logoKindWatch !== 'none' ? (
          <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('workspace.authentication.customProviders.fields.logoPreview')}
            </Text>
            <ProviderLogo
              value={
                logoKindWatch === 'preset'
                  ? logoPresetWatch
                    ? presetValue(logoPresetWatch)
                    : ''
                  : (logoUrlWatch ?? '')
              }
              size={24}
            />
          </Flex>
        ) : null}

        <span className={styles.sectionLabel}>
          {t('workspace.authentication.customProviders.sections.connection')}
        </span>
        {protocolWatch === 'oidc' || protocolWatch === 'oauth2' ? (
          <>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={t(
                    'workspace.authentication.customProviders.fields.clientId'
                  )}
                  name="client_id"
                  rules={[
                    { required: true, message: t('required_field_error') }
                  ]}
                >
                  <Input placeholder="cli_8f3a1b9d2c4e7f6a" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <LabelWithHint
                      label={t(
                        'workspace.authentication.customProviders.fields.clientSecret'
                      )}
                      hint={
                        isEdit
                          ? t(
                              'workspace.authentication.customProviders.fields.clientSecretHelp'
                            )
                          : undefined
                      }
                    />
                  }
                  name="client_secret"
                >
                  <Input.Password
                    placeholder={isEdit ? MASKED_SECRET_PLACEHOLDER : ''}
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={t(
                    'workspace.authentication.customProviders.fields.scope'
                  )}
                  name="scope"
                >
                  <Input placeholder="openid email profile" />
                </Form.Item>
              </Col>
              {protocolWatch === 'oidc' ? (
                <Col span={12}>
                  <Form.Item
                    label={
                      <LabelWithHint
                        label={t(
                          'workspace.authentication.customProviders.fields.issuerUrl'
                        )}
                        hint={t(
                          'workspace.authentication.customProviders.fields.issuerUrlHelp'
                        )}
                      />
                    }
                    name="issuer_url"
                    rules={[
                      { required: true, message: t('required_field_error') }
                    ]}
                  >
                    <Input placeholder="https://example.auth0.com/" />
                  </Form.Item>
                </Col>
              ) : null}
            </Row>
          </>
        ) : null}

        {protocolWatch === 'oidc' ? (
          <Collapse
            ghost
            size="small"
            className={styles.advancedCollapse}
            items={[
              {
                key: 'oidc-advanced',
                label: t(
                  'workspace.authentication.customProviders.sections.oidcAdvanced'
                ),
                children: (
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        label={t(
                          'workspace.authentication.customProviders.fields.authorizeUrl'
                        )}
                        name="authorize_url"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t(
                          'workspace.authentication.customProviders.fields.tokenUrl'
                        )}
                        name="token_url"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t(
                          'workspace.authentication.customProviders.fields.userinfoUrl'
                        )}
                        name="userinfo_url"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t(
                          'workspace.authentication.customProviders.fields.jwksUrl'
                        )}
                        name="jwks_url"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                )
              }
            ]}
          />
        ) : null}

        {protocolWatch === 'oauth2' ? (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label={t(
                  'workspace.authentication.customProviders.fields.authorizeUrl'
                )}
                name="authorize_url"
                rules={[{ required: true, message: t('required_field_error') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t(
                  'workspace.authentication.customProviders.fields.tokenUrl'
                )}
                name="token_url"
                rules={[{ required: true, message: t('required_field_error') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={t(
                  'workspace.authentication.customProviders.fields.userinfoUrl'
                )}
                name="userinfo_url"
                rules={[{ required: true, message: t('required_field_error') }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        ) : null}

        {protocolWatch === 'saml2' ? (
          <>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={t(
                    'workspace.authentication.customProviders.fields.samlEntityId'
                  )}
                  name="saml_entity_id"
                  rules={[
                    { required: true, message: t('required_field_error') }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t(
                    'workspace.authentication.customProviders.fields.samlSsoUrl'
                  )}
                  name="saml_sso_url"
                  rules={[
                    { required: true, message: t('required_field_error') }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.samlSloUrl'
              )}
              name="saml_slo_url"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={
                <LabelWithHint
                  label={t(
                    'workspace.authentication.customProviders.fields.samlX509Cert'
                  )}
                  hint={t(
                    'workspace.authentication.customProviders.fields.samlX509CertHelp'
                  )}
                />
              }
              name="saml_x509_cert"
              rules={[{ required: true, message: t('required_field_error') }]}
            >
              <Input.TextArea
                rows={5}
                placeholder="-----BEGIN CERTIFICATE-----"
              />
            </Form.Item>
          </>
        ) : null}

        <span className={styles.sectionLabel}>
          {t(
            'workspace.authentication.customProviders.sections.attributeMapping'
          )}
        </span>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.emailClaim'
              )}
              name="email_claim"
              rules={[{ required: true, message: t('required_field_error') }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.firstNameClaim'
              )}
              name="first_name_claim"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t(
                'workspace.authentication.customProviders.fields.lastNameClaim'
              )}
              name="last_name_claim"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Space size={6} align="center" style={{ marginTop: 4 }}>
          <span className={styles.sectionLabel} style={{ margin: 0 }}>
            {t(
              'workspace.authentication.customProviders.sections.provisioning'
            )}
          </span>
        </Space>
        <Form.Item
          label={t(
            'workspace.authentication.customProviders.fields.provisioningOnFirstSignIn'
          )}
          name="provisioning_mode"
          style={{ marginTop: 8 }}
        >
          <Radio.Group disabled>
            <Flex vertical gap={6}>
              <Radio value="just_in_time">
                <Text strong style={{ fontSize: 12.5 }}>
                  {t(
                    'workspace.authentication.customProviders.provisioning.jitTitle'
                  )}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block' }}
                >
                  {t(
                    'workspace.authentication.customProviders.provisioning.jitDescription'
                  )}
                </Text>
              </Radio>
              <Radio value="approval">
                <Text strong style={{ fontSize: 12.5 }}>
                  {t(
                    'workspace.authentication.customProviders.provisioning.approvalTitle'
                  )}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block' }}
                >
                  {t(
                    'workspace.authentication.customProviders.provisioning.approvalDescription'
                  )}
                </Text>
              </Radio>
              <Radio value="explicit">
                <Text strong style={{ fontSize: 12.5 }}>
                  {t(
                    'workspace.authentication.customProviders.provisioning.explicitTitle'
                  )}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block' }}
                >
                  {t(
                    'workspace.authentication.customProviders.provisioning.explicitDescription'
                  )}
                </Text>
              </Radio>
            </Flex>
          </Radio.Group>
        </Form.Item>

        <span className={styles.sectionLabel}>
          {t('workspace.authentication.customProviders.sections.callbackUrls')}
        </span>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={t('workspace.authentication.customProviders.callbacksHint')}
        />
        <Flex vertical gap={6}>
          <CallbackUrlRow
            label={t(
              'workspace.authentication.customProviders.callbacks.redirect'
            )}
            value={
              editing?.slug
                ? `${window.location.origin}/authentication/callback/${editing.slug}`
                : `${window.location.origin}/authentication/callback/{slug}`
            }
          />
          <CallbackUrlRow
            label={t(
              'workspace.authentication.customProviders.callbacks.logout'
            )}
            value={`${window.location.origin}/authentication/logout`}
          />
          <CallbackUrlRow
            label={t(
              'workspace.authentication.customProviders.callbacks.metadata'
            )}
            value={
              editing?.slug
                ? `${window.location.origin}/authentication/idp/${editing.slug}/.well-known/metadata`
                : `${window.location.origin}/authentication/idp/{slug}/.well-known/metadata`
            }
          />
        </Flex>

        {isEdit && editing ? (
          <Flex
            align="center"
            justify="space-between"
            gap={8}
            style={{ marginTop: 16 }}
          >
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('workspace.authentication.customProviders.lastSyncLabel')}
            </Text>
            <Tag style={{ marginInlineEnd: 0 }}>
              <DateTimeCell value={editing.date_updated} variant="dateTime" />
            </Tag>
          </Flex>
        ) : null}
      </Form>
    </Drawer>
  )
}

type EnableInlineProps = {
  label: string
  checked?: boolean
  onChange?: (value: boolean) => void
}

/** Compact "Enabled" toggle for the drawer footer. */
const EnableInline = ({ label, checked, onChange }: EnableInlineProps) => {
  return (
    <Space size={6} align="center">
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Switch
        size="small"
        checked={checked}
        onChange={(next) => onChange?.(next)}
      />
    </Space>
  )
}
